/**
 * <RootErrorFallback> — the render-prop fallback for the route-root
 * `<Sentry.ErrorBoundary>` in `src/routes/__root.tsx`.
 *
 * Why this exists
 * ---------------
 * `<ChatErrorBoundary>` (Stream G) catches per-tool-card render errors
 * INSIDE the chat dispatcher. But Wave-17's blank-page bug crashed
 * BEFORE any tool card mounted — the throw came from the route shell
 * itself, far above the chat tree, so the per-card boundary never had
 * a chance. This component is the catch-all safety net: any uncaught
 * React render error anywhere in the routed tree falls through to
 * here instead of leaving the user staring at a blank document.
 *
 * Sentry capture happens automatically inside `<Sentry.ErrorBoundary>`
 * — we do NOT need to call `captureException` here; the boundary itself
 * ships the event. The component's only job is the user-facing UX.
 *
 * Dev vs prod
 * -----------
 * Developers running `vite dev` need stack hints in-page; building
 * owners hitting the prod build should never see internals. We gate
 * `error.message` on `import.meta.env.MODE === "development"` —
 * matches the pattern in `sentry.ts` which uses `MODE` as the Sentry
 * `environment` tag. Vite sets `MODE` to "production" for built
 * artifacts whether they're served from dev cloud or prod cloud, so
 * the dev-cloud deploy still hides internals. That's intentional —
 * dev cloud is shared with real owner users for QA.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";

import { Button } from "@/components/shared/ui/button";

export interface RootErrorFallbackProps {
  /** The error instance Sentry captured. May be any thrown value. */
  readonly error: unknown;
  /**
   * Sentry's render-prop reset hook — clears the boundary's internal
   * error state. We call it BEFORE `window.location.reload()` so that
   * if the reload is somehow blocked the boundary at least tries to
   * re-render with fresh state.
   */
  readonly resetError: () => void;
}

const RootErrorFallback: React.FC<RootErrorFallbackProps> = ({
  error,
  resetError,
}) => {
  const isDev = import.meta.env.MODE === "development";
  // Coerce non-Error throws (strings, objects) to a readable string so
  // the dev-only stack hint never renders `[object Object]`.
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
      ? error
      : "Unknown error";

  const handleReload = (): void => {
    resetError();
    window.location.reload();
  };

  return (
    <div
      role="alert"
      data-testid="root-error-fallback"
      className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center text-foreground"
    >
      <div className="max-w-md space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          Something went wrong
        </h1>
        <p className="text-sm text-muted-foreground">
          We&apos;ve logged this and will fix it. You can reload or go back
          to the dashboard.
        </p>
        <div className="flex flex-col items-center justify-center gap-2 pt-2 sm:flex-row">
          <Button onClick={handleReload} variant="default">
            Reload
          </Button>
          <Button asChild variant="outline">
            <Link to="/portal">Go to dashboard</Link>
          </Button>
        </div>
        {isDev ? (
          <pre
            data-testid="root-error-fallback-dev-message"
            className="mt-4 max-h-48 overflow-auto rounded-md border border-dashed border-muted bg-muted/40 px-3 py-2 text-left font-mono text-xs text-muted-foreground"
          >
            {message}
          </pre>
        ) : null}
      </div>
    </div>
  );
};

export default RootErrorFallback;
